import Button from 'components/Button';
import Dropdown from 'components/Form/Dropdown';
import FormResult from 'components/Form/FormResult';
import SectionHeader from 'components/SectionHeader';
import TableStatic from 'components/Table/TableStatic';
import useBeforeUnload from 'hooks/useBeforeUnload';
import {FC} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {jt, t} from 'ttag';
import {FormFields} from 'types/utils';
import {onApiFormError, onApiKeySuccess} from 'utils/request/result';

import {useChangeCompanyEmail} from '../services';
import {Email, Employee} from '../types';

export type NotificationAddressesProps = {
    emails: Array<Email>;
    notificationAddresses: Array<Employee>;
};

type NotificationAddressesFormData = FormFields<Record<string, string>>;

const NotificationAddresses: FC<NotificationAddressesProps> = ({
    emails,
    notificationAddresses,
}) => {
    const {refreshData} = useBackend();

    const methods = useForm<NotificationAddressesFormData>({
        defaultValues: Object.fromEntries(
            notificationAddresses.map((address) => [
                address.mid,
                address.email,
            ]),
        ),
    });
    const {
        formState: {isDirty},
        getValues,
        reset,
        setValue,
    } = methods;

    useBeforeUnload(isDirty);

    const {buttonProps, mutateAsync} = useChangeCompanyEmail();

    const onSubmit = (entry: Employee) => {
        const targetEmailMID = emails.find(
            (email) => email.email === getValues(entry.mid),
        )?.mid;
        if (!targetEmailMID) return;

        mutateAsync({
            email: targetEmailMID,
            employee: entry.mid,
            requestId: entry.mid,
        })
            .then(onApiKeySuccess(methods))
            .catch(onApiFormError(methods))
            .finally(async () => {
                await refreshData(window.Urls.email_settings_view());
                reset(getValues(), {keepErrors: true});
            });
    };

    const getEmailOptions = () =>
        emails.map(({disabled, email, status}) => ({
            disabled,
            label: email,
            status,
            value: email,
        }));

    const onChangeEmail = (entry: Employee) => (email?: string) => {
        if (email) setValue(entry.mid, email);
    };

    const notificationPreferencesLink = (
        <a
            href={window.Urls.notifications_view()}
            key="notification-preference-link"
        >
            {t`Notification Preferences`}
        </a>
    );

    return (
        <FormProvider {...methods}>
            <form>
                <SectionHeader
                    className="mb-2 mt-6"
                    hideHR={true}
                    level={2}
                    subtitle={jt`Company accounts-related activity notifications are sent to the email address you have set here. You can select from the email addresses you have added above.  If you want to change the kinds of notifications you're receiving, please go to ${notificationPreferencesLink}.`}
                    title={t`Addresses for Account-related Notifications`}
                />
                <FormResult className="my-2" />
                <TableStatic
                    aria-label={t`Addresses for Account-related Notifications`}
                >
                    <TableStatic.Header>
                        <TableStatic.HeaderRow>
                            <TableStatic.Column>
                                {t`Company`}
                            </TableStatic.Column>
                            <TableStatic.Column>{t`Email`}</TableStatic.Column>
                            <TableStatic.Column />
                        </TableStatic.HeaderRow>
                    </TableStatic.Header>
                    <TableStatic.Body>
                        {notificationAddresses.map((entry) => (
                            <TableStatic.Row key={entry.mid}>
                                <TableStatic.Cell>
                                    {entry.company}
                                </TableStatic.Cell>
                                <TableStatic.Cell>
                                    <Dropdown
                                        aria-label={`${entry.company}-email`}
                                        hasClearSelection={false}
                                        isCompact={true}
                                        isSearchable={true}
                                        name={entry.mid}
                                        onChange={onChangeEmail(entry)}
                                        options={getEmailOptions()}
                                        type="select"
                                    />
                                </TableStatic.Cell>
                                <TableStatic.Cell>
                                    <div className="flex flex-row-reverse">
                                        <Button
                                            aria-label={`${entry.company} button`}
                                            disabled={
                                                getValues(entry.mid) ===
                                                entry.email
                                            }
                                            kind="secondary"
                                            onClick={() => onSubmit(entry)}
                                            size="sm"
                                            {...buttonProps[entry.mid]}
                                        >
                                            {t`Save`}
                                        </Button>
                                    </div>
                                </TableStatic.Cell>
                            </TableStatic.Row>
                        ))}
                    </TableStatic.Body>
                </TableStatic>
            </form>
        </FormProvider>
    );
};

export default NotificationAddresses;
